<template>
  <div>
    <!-- 页面标题 -->
    <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 20px;">
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>支出管理</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 搜索和操作区域 -->
    <el-card style="margin-bottom: 20px;">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="支出类别">
          <el-select v-model="searchForm.category" placeholder="请选择" clearable>
            <el-option
              v-for="(label, value) in expenseCategories"
              :key="value"
              :label="label"
              :value="value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadExpenses" icon="el-icon-search">查询</el-button>
          <el-button @click="resetSearch" icon="el-icon-refresh">重置</el-button>
          <el-button type="success" @click="openAddDialog" icon="el-icon-plus">添加支出</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 支出列表 -->
    <el-card>
      <div slot="header">
        <span>支出记录列表</span>
        <div style="float: right;">
          <el-statistic 
            title="当期支出总额" 
            :value="totalExpense" 
            :precision="2"
            prefix="¥">
          </el-statistic>
        </div>
      </div>

      <el-table 
        :data="expenses" 
        v-loading="loading"
        stripe
        border>
        <el-table-column prop="id" label="编号" width="80"></el-table-column>
        <el-table-column prop="category" label="类别" width="120">
          <template slot-scope="scope">
            <el-tag :type="getCategoryTagType(scope.row.category)">
              {{ expenseCategories[scope.row.category] || scope.row.category }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="120" align="right">
          <template slot-scope="scope">
            <span class="expense-amount">¥{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="occurredAt" label="发生时间" width="180">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.occurredAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="note" label="备注" show-overflow-tooltip min-width="200"></el-table-column>
        <el-table-column prop="createdUser" label="创建人" width="100">
          <template slot-scope="scope">
            {{ scope.row.createdUser ? scope.row.createdUser.userName : '系统' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="360" fixed="right">
          <template slot-scope="scope">
            <el-tooltip effect="dark" content="查看支出详情" placement="top" :enterable="false" :open-delay="500">
              <el-button type="success" @click="viewDetail(scope.row)" style="font-size: 18px;">
                详情
              </el-button>
            </el-tooltip>
            <el-tooltip effect="dark" content="修改支出信息" placement="top" :enterable="false" :open-delay="500">
              <el-button type="primary" @click="editExpense(scope.row)" style="font-size: 18px;">
                编辑
              </el-button>
            </el-tooltip>
            <el-tooltip effect="dark" content="删除支出记录" placement="top" :enterable="false" :open-delay="500">
              <el-button type="danger" @click="deleteExpense(scope.row)" style="font-size: 18px;">
                删除
              </el-button>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageInfo.pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageInfo.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pageInfo.total"
        style="margin-top: 20px; text-align: left;">
      </el-pagination>
    </el-card>

    <!-- 添加/编辑支出对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="50%"
      @close="resetForm">
      <el-form :model="expenseForm" :rules="formRules" ref="expenseForm" label-width="100px">
        <el-form-item label="支出类别" prop="category">
          <el-select v-model="expenseForm.category" placeholder="请选择支出类别" style="width: 100%;">
            <el-option
              v-for="(label, value) in expenseCategories"
              :key="value"
              :label="label"
              :value="value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="支出金额" prop="amount">
          <el-input-number 
            v-model="expenseForm.amount" 
            :precision="2"
            :min="0.01"
            :max="999999.99"
            style="width: 100%;"
            placeholder="请输入支出金额">
          </el-input-number>
        </el-form-item>
        <el-form-item label="发生时间" prop="occurredAt">
          <el-date-picker
            v-model="expenseForm.occurredAt"
            type="datetime"
            placeholder="请选择发生时间"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="备注说明">
          <el-input
            v-model="expenseForm.note"
            type="textarea"
            :rows="3"
            placeholder="请输入备注说明">
          </el-input>
        </el-form-item>
        <!-- 文件上传功能已隐藏 -->
        <!-- <el-form-item label="附件">
          <el-upload
            ref="upload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :file-list="fileList"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-remove="handleFileRemove">
            <el-button size="small" type="primary" icon="el-icon-upload">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">只能上传jpg/png/pdf文件，且不超过5MB</div>
          </el-upload>
        </el-form-item> -->
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog
      title="支出详情"
      :visible.sync="detailVisible"
      width="60%">
      <el-descriptions :column="2" border v-if="currentExpense">
        <el-descriptions-item label="编号">{{ currentExpense.id }}</el-descriptions-item>
        <el-descriptions-item label="支出类别">
          <el-tag :type="getCategoryTagType(currentExpense.category)">
            {{ expenseCategories[currentExpense.category] }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="支出金额">
          <span class="expense-amount">¥{{ formatMoney(currentExpense.amount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="发生时间">{{ formatDateTime(currentExpense.occurredAt) }}</el-descriptions-item>
        <el-descriptions-item label="备注说明" :span="2">{{ currentExpense.note || '无' }}</el-descriptions-item>
        <el-descriptions-item label="创建人">{{ currentExpense.createdUser?.userName || '系统' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(currentExpense.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间" :span="2">{{ formatDateTime(currentExpense.updateTime) }}</el-descriptions-item>
        <!-- 附件显示功能已隐藏 -->
        <!-- <el-descriptions-item label="附件" :span="2" v-if="currentExpense.attachmentUrl">
          <el-link :href="currentExpense.attachmentUrl" target="_blank" type="primary">查看附件</el-link>
        </el-descriptions-item> -->
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "ExpenseManagement",
  data() {
    return {
      searchForm: {
        category: '',
        dateRange: []
      },
      expenses: [],
      loading: false,
      pageInfo: {
        pageNum: 1,
        pageSize: 20,
        total: 0
      },
      totalExpense: 0,
      expenseCategories: {},
      
      // 对话框相关
      dialogVisible: false,
      detailVisible: false,
      dialogTitle: '',
      isEdit: false,
      currentExpense: null,
      submitting: false,
      
      // 表单相关
      expenseForm: {
        id: null,
        category: '',
        amount: null,
        occurredAt: '',
        note: '',
        attachmentUrl: ''
      },
      formRules: {
        category: [
          { required: true, message: '请选择支出类别', trigger: 'change' }
        ],
        amount: [
          { required: true, message: '请输入支出金额', trigger: 'blur' },
          { type: 'number', min: 0.01, message: '金额必须大于0', trigger: 'blur' }
        ],
        occurredAt: [
          { required: true, message: '请选择发生时间', trigger: 'change' }
        ]
      },
      
      // 文件上传相关
      uploadUrl: process.env.VUE_APP_API_BASE_URL + '/upload',
      uploadHeaders: {},
      fileList: []
    }
  },
  created() {
    this.loadExpenseCategories()
    this.loadExpenses()
    this.setupUploadHeaders()
  },
  methods: {
    async loadExpenseCategories() {
      try {
        const { data: res } = await axios.get('api/admin/finance/expense/categories')
        if (res.code === 200) {
          this.expenseCategories = res.data
        }
      } catch (error) {
        console.error('加载支出类别失败:', error)
      }
    },

    async loadExpenses() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pageInfo.pageNum,
          pageSize: this.pageInfo.pageSize,
          category: this.searchForm.category
        }

        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startDate = this.searchForm.dateRange[0]
          params.endDate = this.searchForm.dateRange[1]
        }

        const { data: res } = await axios.get('api/admin/finance/expense', { params })
        console.log('支出管理API响应:', res)
        
        if (res.code === 200) {
          // 检查数据格式
          if (res.data && Array.isArray(res.data.list)) {
            this.expenses = res.data.list
            this.pageInfo.total = res.data.total || 0
          } else if (res.data && Array.isArray(res.data)) {
            // 如果直接返回数组
            this.expenses = res.data
            this.pageInfo.total = res.data.length
          } else {
            console.warn('支出数据格式异常:', res.data)
            this.expenses = []
            this.pageInfo.total = 0
          }
          
          console.log('设置支出数据:', this.expenses.length, '条记录')
          
          // 计算当期支出总额
          this.calculateTotalExpense()
        } else {
          this.$message.error('获取支出记录失败: ' + res.message)
        }
      } catch (error) {
        console.error('加载支出记录失败:', error)
        this.$message.error('加载支出记录失败')
      } finally {
        this.loading = false
      }
    },

    calculateTotalExpense() {
      this.totalExpense = this.expenses.reduce((total, expense) => {
        return total + (expense.amount || 0)
      }, 0)
    },

    resetSearch() {
      this.searchForm = {
        category: '',
        dateRange: []
      }
      this.pageInfo.pageNum = 1
      this.loadExpenses()
    },

    openAddDialog() {
      this.dialogTitle = '添加支出'
      this.isEdit = false
      this.dialogVisible = true
      this.resetForm()
    },

    editExpense(row) {
      this.dialogTitle = '编辑支出'
      this.isEdit = true
      this.currentExpense = row
      
      this.expenseForm = {
        id: row.id,
        category: row.category,
        amount: row.amount,
        occurredAt: row.occurredAt,
        note: row.note,
        attachmentUrl: row.attachmentUrl
      }
      
      // 文件列表设置已隐藏
      // if (row.attachmentUrl) {
      //   this.fileList = [{
      //     name: '附件',
      //     url: row.attachmentUrl
      //   }]
      // }
      
      this.dialogVisible = true
    },

    async deleteExpense(row) {
      try {
        await this.$confirm('确认删除这条支出记录？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const { data: res } = await axios.delete(`api/admin/finance/expense/${row.id}`)
        if (res.code === 200) {
          this.$message.success('删除成功')
          this.loadExpenses()
        } else {
          this.$message.error('删除失败: ' + res.message)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除支出记录失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    viewDetail(row) {
      this.currentExpense = row
      this.detailVisible = true
    },

    async submitForm() {
      try {
        await this.$refs.expenseForm.validate()
        
        this.submitting = true
        
        // 文件上传处理已隐藏
        // if (this.fileList.length > 0 && this.fileList[0].raw) {
        //   // 有新文件需要上传
        //   await this.uploadFile()
        // }

        const url = this.isEdit ? 'api/admin/finance/expense' : 'api/admin/finance/expense'
        const method = this.isEdit ? 'put' : 'post'
        
        const { data: res } = await axios[method](url, this.expenseForm)
        if (res.code === 200) {
          this.$message.success(this.isEdit ? '编辑成功' : '添加成功')
          this.dialogVisible = false
          this.loadExpenses()
        } else {
          this.$message.error((this.isEdit ? '编辑' : '添加') + '失败: ' + res.message)
        }
      } catch (error) {
        console.error('提交表单失败:', error)
        this.$message.error('操作失败')
      } finally {
        this.submitting = false
      }
    },

    resetForm() {
      this.expenseForm = {
        id: null,
        category: '',
        amount: null,
        occurredAt: '',
        note: '',
        attachmentUrl: ''
      }
      // this.fileList = [] // 文件列表重置已隐藏
      
      if (this.$refs.expenseForm) {
        this.$refs.expenseForm.resetFields()
      }
    },

    setupUploadHeaders() {
      const token = window.sessionStorage.getItem('token')
      if (token) {
        this.uploadHeaders['Authorization'] = token
      }
    },

    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1) // 只保留最后一个文件
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList
      this.expenseForm.attachmentUrl = ''
    },

    async uploadFile() {
      // 这里简化处理，实际项目中需要实现文件上传逻辑
      // 模拟上传成功
      this.expenseForm.attachmentUrl = '/uploads/attachment_' + Date.now() + '.pdf'
    },

    handleSizeChange(size) {
      this.pageInfo.pageSize = size
      this.pageInfo.pageNum = 1
      this.loadExpenses()
    },

    handleCurrentChange(page) {
      this.pageInfo.pageNum = page
      this.loadExpenses()
    },

    getCategoryTagType(category) {
      const typeMap = {
        'FILM_COST': 'success',
        'EQUIPMENT': 'info',
        'SALARY': 'warning',
        'RENT': 'danger',
        'OTHER': ''
      }
      return typeMap[category] || ''
    },

    formatMoney(amount) {
      if (!amount && amount !== 0) return '0.00'
      return Number(amount).toFixed(2)
    },

    formatDateTime(dateTime) {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.search-form {
  margin-bottom: 0;
}

.expense-amount {
  color: #F56C6C;
  font-weight: bold;
}

.dialog-footer {
  text-align: right;
}

.el-statistic {
  --el-statistic-content-font-size: 18px;
}
</style>
